import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { SubscriptionInfo } from '@/lib/utils';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
  subscription: SubscriptionInfo | null;
  loadingSubscription: boolean;
  refreshSubscription: () => Promise<SubscriptionInfo | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const refreshingRef = useRef(false);
  const lastRefreshRef = useRef<number>(0);
  const isSigningOutRef = useRef(false);
  const refreshAttemptsRef = useRef(0);
  const lastResetTimeRef = useRef(Date.now());

  const refreshSubscription = useCallback(async (): Promise<SubscriptionInfo | null> => {
    if (!user) {
      setSubscription(null);
      return null;
    }
    
    // Circuit breaker: detect rapid refresh attempts
    const now = Date.now();
    if (now - lastResetTimeRef.current > 60000) {
      // Reset counter every minute
      refreshAttemptsRef.current = 0;
      lastResetTimeRef.current = now;
    }

    refreshAttemptsRef.current++;

    // If more than 5 attempts in a minute, something is wrong
    if (refreshAttemptsRef.current > 5) {
      console.error('Circuit breaker: Too many refresh attempts, forcing reset');
      if (!isSigningOutRef.current) {
        isSigningOutRef.current = true;
        setSubscription(null);
        
        // Clean localStorage
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-')) {
            localStorage.removeItem(key);
          }
        });
        
        await supabase.auth.signOut({ scope: 'local' });
        setTimeout(() => { isSigningOutRef.current = false; }, 5000);
      }
      return null;
    }
    
    // Prevent overlapping or overly-frequent refreshes to avoid UI flicker
    if (refreshingRef.current) {
      return subscription;
    }
    if (now - lastRefreshRef.current < 10000) {
      return subscription;
    }
    refreshingRef.current = true;
    lastRefreshRef.current = now;

    try {
      setLoadingSubscription(true);
      // Retired build: read the account as the new app left it; nothing here changes it.
      const { data, error } = await supabase
        .from('subscribers')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      const info: SubscriptionInfo = {
        subscribed: data?.subscribed ?? false,
        status: data?.status ?? 'free',
        plan: data?.plan ?? 'free',
        monthly_credits: data?.monthly_credits ?? 0,
        monthly_searches_used: data?.monthly_searches_used ?? 0,
        trial_expires_at: data?.trial_expires_at ?? null,
        subscription_end: data?.subscription_end ?? null,
        ab_test_group: data?.ab_test_group ?? null,
        auto_discount_code: data?.auto_discount_code ?? null,
        auto_discount_expires_at: data?.auto_discount_expires_at ?? null,
        trial_reset_2026: data?.trial_reset_2026 ?? null,
      };
      setSubscription(info);
      return info;
    } catch (error: any) {
      console.error('refreshSubscription error', error);
      
      // Detect authentication errors and clean session automatically
      const errorMessage = error?.message?.toLowerCase() || '';
      const isAuthError = 
        errorMessage.includes('session') ||
        errorMessage.includes('jwt') ||
        errorMessage.includes('authentication') ||
        errorMessage.includes('unauthorized');
      
      if (isAuthError && !isSigningOutRef.current) {
        console.error('Error de autenticación detectado, limpiando sesión...');
        isSigningOutRef.current = true;
        
        // Set subscription to null immediately to prevent UI issues
        setSubscription(null);
        
        // Clean localStorage
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-')) {
            localStorage.removeItem(key);
          }
        });
        
        // Force signOut (redirect will be handled by useSessionMonitor)
        await supabase.auth.signOut({ scope: 'local' });
        
        // Reset flag after a delay
        setTimeout(() => { isSigningOutRef.current = false; }, 5000);
      }
      
      return null;
    } finally {
      setLoadingSubscription(false);
      refreshingRef.current = false;
    }
  }, [user?.id]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          // Only refresh on explicit sign-in, not on auth updates to prevent loops
          if (event === 'SIGNED_IN') {
            refreshSubscription();
          }
        } else {
          setSubscription(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        refreshSubscription();
      } else {
        setSubscription(null);
      }
    });

    return () => authSub.unsubscribe();
  }, [refreshSubscription]);

  const signIn = async () => {
    // Get the current page to return to after OAuth
    const returnTo = window.location.pathname !== '/auth' ? window.location.pathname : '/';
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth?returnTo=${encodeURIComponent(returnTo)}`
      }
    });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error en signOut:', error);
      }
    } catch (error) {
      console.error('SignOut falló, limpiando manualmente:', error);
    } finally {
      // Always clean localStorage, even if signOut fails
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      // Reset states
      setUser(null);
      setSession(null);
      setSubscription(null);
    }
  };

  const value = {
    user,
    session,
    signIn,
    signOut,
    loading,
    subscription,
    loadingSubscription,
    refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
